/**
 * Admin Feed Producers Service (US-048-08)
 *
 * Client for Journals.API AdminProducersController. Same base-URL routing as
 * adminPostModerationService.ts: VITE_JOURNALS_API_URL when set (deployed
 * builds), else the `/api/journal/...` dev-proxy path.
 *
 *   GET /api/v1/admin/producers
 *   PUT /api/v1/admin/producers/{producer}/settings   { enabled }
 *   PUT /api/v1/admin/producers/_Global/settings      { globalDailyCapPerUser }
 */

import { authenticatedFetch } from '../api/authenticatedFetch';
import { COLDSTART_RETRY_CONFIG } from '../api/resilientFetch';

const JOURNALS_BASE: string = import.meta.env.VITE_JOURNALS_API_URL || '';

const ADMIN_PRODUCERS = JOURNALS_BASE
  ? `${JOURNALS_BASE}/api/v1/admin/producers`
  : '/api/journal/v1/admin/producers';

export type ProducerStatus = 'ok' | 'failing' | 'overdue' | 'disabled' | 'never-run';

export interface SkippedByReason {
  privacyFloor: number;
  consentOff: number;
  dailyCap: number;
  duplicate: number;
  notSubstantive: number;
  disabled: number;
}

export interface ProducerListItem {
  producer: string;
  enabled: boolean;
  isDefault: boolean;
  status: ProducerStatus;
  lastRunAt: string | null;
  published7d: number;
  skipped7d: number;
  skippedByReason7d: SkippedByReason;
}

export interface ProducerListResponse {
  producers: ProducerListItem[];
  globalDailyCapPerUser: number;
  capIsDefault: boolean;
}

/** Non-2xx response. `fieldErrors` carries the ValidationProblem `errors` map of a 400. */
export class ProducersHttpError extends Error {
  readonly status: number;
  readonly fieldErrors: Record<string, string[]>;

  constructor(status: number, message: string, fieldErrors: Record<string, string[]> = {}) {
    super(message);
    this.name = 'ProducersHttpError';
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await authenticatedFetch(
    url,
    {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    },
    { retryConfig: COLDSTART_RETRY_CONFIG },
  );

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      message?: string;
      title?: string;
      errors?: Record<string, string[]>;
    };
    const fieldErrors = body.errors ?? {};
    const firstField = Object.values(fieldErrors)[0]?.[0];
    throw new ProducersHttpError(
      response.status,
      firstField ?? body.message ?? body.title ?? `HTTP ${response.status}: ${response.statusText}`,
      fieldErrors,
    );
  }

  return (await response.json()) as T;
}

export function getProducers(): Promise<ProducerListResponse> {
  return request<ProducerListResponse>(ADMIN_PRODUCERS);
}

export function setProducerEnabled(producer: string, enabled: boolean): Promise<unknown> {
  return request<unknown>(`${ADMIN_PRODUCERS}/${encodeURIComponent(producer)}/settings`, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });
}

export function setGlobalDailyCap(globalDailyCapPerUser: number): Promise<unknown> {
  return request<unknown>(`${ADMIN_PRODUCERS}/_Global/settings`, {
    method: 'PUT',
    body: JSON.stringify({ globalDailyCapPerUser }),
  });
}

// ---------------------------------------------------------------------------
// Producer detail (US-048-09 / 20091)
// ---------------------------------------------------------------------------

export interface ProducerFieldValue<T> {
  value: T;
  isDefault: boolean;
  default: T;
}

export interface ProducerDetail {
  producer: string;
  enabled: ProducerFieldValue<boolean> | null;
  globalDailyCapPerUser: ProducerFieldValue<number> | null;
  thresholds: ProducerFieldValue<number[]> | null;
  idleDays: ProducerFieldValue<number> | null;
  runDay: ProducerFieldValue<string> | null;
  runHourUtc: ProducerFieldValue<number> | null;
  minGroupSize: number;
  nextScheduledRunUtc: string | null;
}

export interface ProducerRunSummary {
  runKey: string;
  startedAt: string;
  lastUpdatedAt: string;
  published: number;
  skippedPrivacyFloor: number;
  skippedConsentOff: number;
  skippedDailyCap: number;
  skippedDuplicate: number;
  skippedNotSubstantive: number;
  skippedDisabled: number;
  failed: number;
}

export interface ProducerEntry {
  journalId: number;
  title: string;
  summary: string;
  createdAt: string | null;
  isHidden: boolean;
  isDeleted: boolean;
  visibility: 'peers' | 'private' | 'aggregate';
  recipientCount: number;
}

export interface ProducerSettingAudit {
  field: string;
  oldValue?: string | null;
  newValue?: string | null;
  changedBy: string;
  changedAt: string;
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  hasMore: boolean;
}

export type ProducerSettingsPatch = Partial<
  Record<'enabled' | 'thresholds' | 'idleDays' | 'runDay' | 'runHourUtc', unknown>
>;

export function getProducerDetail(producer: string): Promise<ProducerDetail> {
  return request<ProducerDetail>(`${ADMIN_PRODUCERS}/${encodeURIComponent(producer)}`);
}

export function updateProducerSettings(
  producer: string,
  patch: ProducerSettingsPatch,
): Promise<ProducerDetail> {
  return request<ProducerDetail>(`${ADMIN_PRODUCERS}/${encodeURIComponent(producer)}/settings`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

export function getProducerRuns(
  producer: string,
  page = 0,
  pageSize = 20,
): Promise<PagedResult<ProducerRunSummary>> {
  return request<PagedResult<ProducerRunSummary>>(
    `${ADMIN_PRODUCERS}/${encodeURIComponent(producer)}/runs?page=${page}&pageSize=${pageSize}`,
  );
}

export function getProducerEntries(producer: string, take = 10): Promise<ProducerEntry[]> {
  return request<ProducerEntry[]>(
    `${ADMIN_PRODUCERS}/${encodeURIComponent(producer)}/entries?take=${take}`,
  );
}

export function getProducerAudit(
  producer: string,
  page = 0,
  pageSize = 20,
): Promise<PagedResult<ProducerSettingAudit>> {
  return request<PagedResult<ProducerSettingAudit>>(
    `${ADMIN_PRODUCERS}/${encodeURIComponent(producer)}/audit?page=${page}&pageSize=${pageSize}`,
  );
}
